import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getMonthTotal } from '@/services/expenseService';
import { getPlannedYearTotal } from '@/services/plannedExpenseService';
import { getLastNMonths, formatCurrency, formatMonthYear } from '@/utils/formatters';
import LoadingSpinner from '@/components/shared/LoadingSpinner';

export default function YearlySummary() {
  const year  = String(new Date().getFullYear());
  const months = getLastNMonths(12).reverse(); // chronological

  const [monthlyData, setMonthlyData] = useState([]);
  const [plannedTotal, setPlannedTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      ...months.map(m => getMonthTotal(m).then(t => ({ month: m, total: t }))),
      getPlannedYearTotal(year),
    ]).then(results => {
      const planned = results.pop();
      setMonthlyData(results);
      setPlannedTotal(planned);
    }).finally(() => setLoading(false));
  }, [year]);

  const dailyTotal = monthlyData.reduce((s, m) => s + m.total, 0);
  const grandTotal = dailyTotal + plannedTotal;

  const chartData = monthlyData.map(m => ({
    name: m.month.slice(5), // MM
    amount: m.total,
  }));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <div className="rounded-card p-4 text-white"
        style={{ background: 'linear-gradient(135deg, #263238, #37474F)' }}>
        <p className="text-xs font-bold opacity-70 uppercase tracking-wide mb-3">{year} Grand Total</p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm border-b border-white/10 pb-2">
            <span className="opacity-80">📅 Daily Expenses</span>
            <span className="font-bold">{formatCurrency(dailyTotal)}</span>
          </div>
          <div className="flex justify-between text-sm border-b border-white/10 pb-2">
            <span className="opacity-80">📦 Planned Expenses</span>
            <span className="font-bold text-yellow-300">{formatCurrency(plannedTotal)}</span>
          </div>
          <div className="flex justify-between text-base pt-1">
            <span className="font-bold">Total Annual Spend</span>
            <span className="font-bold text-cyan-300">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Monthly bar */}
      <div className="card">
        <h3 className="text-xs font-bold text-gray-600 mb-3">📊 Monthly Totals — {year}</h3>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <XAxis dataKey="name" tick={{ fontSize: 9 }} />
            <YAxis tick={{ fontSize: 8 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={v => formatCurrency(v)} labelFormatter={l => `Month ${l}`} />
            <Bar dataKey="amount" fill="#1565C0" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Month breakdown table */}
      <div className="card">
        <h3 className="text-xs font-bold text-gray-600 mb-2">Month-wise Breakdown</h3>
        <div className="space-y-1">
          {monthlyData.filter(m => m.total > 0).map(m => (
            <div key={m.month} className="flex justify-between text-xs py-1 border-b border-gray-50">
              <span className="text-gray-600">{formatMonthYear(new Date(`${m.month}-01`))}</span>
              <span className="font-semibold text-primary">{formatCurrency(m.total)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
